import { useMutation, useQuery } from '@apollo/client/react';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import GeneralSelector, { GeneralSelectorOption } from '../picker/GeneralSelector';
import { PaymentProvider, UpdatePaymentProviderConfigInput } from '../../models/bank/PaymentProvider';
import { PaymentProviderConfig } from '../../models/profile';
import { FETCH_AVAILABLE_PAYMENT_PROVIDERS, UPDATE_MY_PAYMENT_PROVIDER_CONFIG } from '../../queries/bank';
import { FetchMyProfile } from '../../queries/profile';

interface FetchAvailablePaymentProvidersResponse {
    providers: PaymentProvider[];
}

interface UpdatePaymentProviderConfigResponse {
    config: {
        profile: {
            payment_provider_config: {
                id: string;
            };
        };
    };
}

interface ModalEditPaymentProviderProps {
    currentConfig?: PaymentProviderConfig;
    onUpdate: () => void;
}

const ModalEditPaymentProvider: React.FC<ModalEditPaymentProviderProps> = ({ currentConfig, onUpdate }) => {
    const [open, setOpen] = useState(false);
    const [accountNumber, setAccountNumber] = useState(currentConfig?.bank_account_number || '');
    const [error, setError] = useState<string | null>(null);

    // Fetch available providers
    const { data: providersData, loading: providersLoading } = useQuery<FetchAvailablePaymentProvidersResponse>(
        FETCH_AVAILABLE_PAYMENT_PROVIDERS
    );

    const providerOptions: GeneralSelectorOption[] = useMemo(() => {
        return providersData?.providers
        .filter((p: PaymentProvider) => p.enabled)
        .map((p: PaymentProvider) => ({
            id: p.id,
            name: p.displayName,
        }))
        .sort((a: GeneralSelectorOption, b: GeneralSelectorOption) => a.name.localeCompare(b.name)) || [];
    }
    , [providersData])

    // Initialize selected provider after providerOptions is available
    const [selectedProvider, setSelectedProvider] = useState<GeneralSelectorOption | undefined>(undefined);

    useEffect(() => {
        setSelectedProvider(currentConfig && providerOptions.length > 0
            ? providerOptions.find(p => p.id === currentConfig.provider_id) ?? undefined
            : undefined)
    }, [providerOptions])

    const [updatePaymentConfig, { loading: updating }] = useMutation<
        UpdatePaymentProviderConfigResponse,
        { input: UpdatePaymentProviderConfigInput }
    >(UPDATE_MY_PAYMENT_PROVIDER_CONFIG, {
        refetchQueries: [{ query: FetchMyProfile }],
        onCompleted: () => {
            setOpen(false);
            setError(null);
            onUpdate();
        },
        onError: (err) => {
            setError(err.message);
        },
    });

    const handleOpen = () => {
        setOpen(true);
        setError(null);
    };

    const handleClose = () => {
        if (!updating) {
            setOpen(false);
            setError(null);
            // Reset to current config
            if (currentConfig) {
                console.log('setting not undefined')
                setSelectedProvider(providerOptions.find(p => p.id === currentConfig.provider_id));
                setAccountNumber(currentConfig.bank_account_number);
            } else {
                console.log('setting undefined')
                setSelectedProvider(undefined);
                setAccountNumber('');
            }
        }
    };

    const handleSave = () => {
        if (!selectedProvider || !accountNumber.trim()) {
            setError('Please fill in all fields');
            return;
        }

        updatePaymentConfig({
            variables: {
                input: {
                    provider_id: selectedProvider.id,
                    bank_account_number: accountNumber.trim(),
                },
            },
        });
    };

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleOpen}
                sx={{ mt: 2 }}
            >
                {currentConfig ? 'Edit Payment Provider' : 'Add Payment Provider'}
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {currentConfig ? 'Edit Payment Provider' : 'Add Payment Provider'}
                        </Typography>
                        <IconButton onClick={handleClose} disabled={updating} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                Payment Provider *
                            </Typography>
                            {providersLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <GeneralSelector
                                    label="Select Provider"
                                    onChange={(value) => { console.log('onchanged value', value); setSelectedProvider(value) }}
                                    data={providerOptions}
                                    placeholder="Select Provider"
                                    initValue={selectedProvider?.id}
                                    clearable={true}
                                />
                            )}
                        </Box>
                        <TextField
                            label="Bank Account Number"
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value)}
                            fullWidth
                            required
                            disabled={updating}
                            helperText="Your bank account number"
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={updating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={updating || !selectedProvider || !accountNumber.trim()}
                    >
                        {updating ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ModalEditPaymentProvider;
